import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, MapPin, Calendar, RefreshCw, ExternalLink, Users, DollarSign, FileText } from 'lucide-react';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';

interface EstimationStats {
  totalUsage: number;
  totalLeads: number;
  totalCost: number;
  totalTokens: number;
  topCities: Array<{ city: string; count: number }>;
  usageByDate: Array<{ date: string; count: number }>;
  leadsByDate: Array<{ date: string; count: number }>;
  detailedList: Array<{
    id: string;
    createdAt: string;
    tokens: number;
    cost: number;
    leadId: string | null;
    city: string | null;
  }>;
}

export const EstimationStatsPanel: React.FC = () => {
  const { toast } = useModal();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EstimationStats | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await storage.getEstimationStats(startDate, endDate);
      setStats(data);
    } catch (e) {
      console.error('Error loading estimation stats:', e);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement des statistiques d'estimation...
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8 text-center text-slate-500">
        Aucune donnée disponible
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-slate-700" />
            Statistiques Estimateur Public
          </h2>
          <p className="text-slate-500">Suivi des utilisations de l'estimateur public</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </button>
      </div>

      {/* Date filters */}
      <div className="mb-6 flex gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Date de début</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Date de fin</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Total Utilisations</span>
            <TrendingUp className="w-5 h-5 text-amber-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900">{formatNumber(stats.totalUsage)}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Leads Créés</span>
            <Users className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900">{formatNumber(stats.totalLeads)}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Coût Total</span>
            <DollarSign className="w-5 h-5 text-red-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900">{formatCurrency(stats.totalCost)}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Tokens Total</span>
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900">{formatNumber(stats.totalTokens)}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Usage by date */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-amber-600" />
            Utilisations par Date
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.usageByDate.length > 0 ? (
              stats.usageByDate.map((item) => (
                <div key={item.date} className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">{formatDate(item.date)}</span>
                  <span className="font-semibold text-slate-900">{item.count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>

        {/* Top cities */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-amber-600" />
            Top Villes
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.topCities.length > 0 ? (
              stats.topCities.map((item, index) => (
                <div key={item.city} className="flex items-center justify-between py-2 border-b border-slate-100">
                  <div className="flex items-center">
                    <span className="text-xs font-bold text-slate-400 w-6">{index + 1}.</span>
                    <span className="text-sm text-slate-600">{item.city}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{item.count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>
      </div>

      {/* Detailed list */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Liste Détaillée des Utilisations</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Ville</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Tokens</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Coût</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Lead</th>
              </tr>
            </thead>
            <tbody>
              {stats.detailedList.length > 0 ? (
                stats.detailedList.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-600">{formatDateTime(item.createdAt)}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{item.city || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600 text-right">{formatNumber(item.tokens)}</td>
                    <td className="py-3 px-4 text-sm text-slate-600 text-right">{formatCurrency(item.cost)}</td>
                    <td className="py-3 px-4 text-center">
                      {item.leadId ? (
                        <a
                          href={`#marketing_leads`}
                          onClick={(e) => {
                            e.preventDefault();
                            // Store lead ID to highlight it
                            sessionStorage.setItem('highlightLeadId', item.leadId!);
                            // Navigate to marketing leads panel
                            window.location.hash = 'marketing_leads';
                            window.location.reload();
                          }}
                          className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium text-sm"
                        >
                          Voir le lead
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-400 text-sm">
                    Aucune utilisation trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
